<template>
  <div>
    <div
      class="kk-designer__grid"
      :class="[
        `kk-designer__grid-${rangeDragging.cursorType}`,
        { 'kk-designer__grid--formatPainter': formatPainterData.isFormatPainterActive },
      ]"
      :style="{
        top: startYPosition + 'px',
        left: startXPosition + 'px',
        gridTemplateColumns: gridColumns,
        gridTemplateRows: gridRows,
      }"
      @mouseleave="hideComment"
      @mousedown="mouseDown"
      @dblclick="dblClick"
      @contextmenu="rightClick"
    >
      <hotkey-component :get-px-from-column-index="getPxFromColumnIndex" :get-px-from-row-index="getPxFromRowIndex" />
      <column-letter
        v-for="(item, index) in visibleColumns"
        :key="item.id"
        :grid-index="index"
        :index="hScroll.index + index"
        :scope-on-designer="scopeOnDesigner"
        :is-column-active="isColumnActive(hScroll.index + index)"
        :remove-white-space="removeColumnWhiteSpace(hScroll.index + index, index)"
      />
      <row-number
        v-for="(item, index) in visibleRows"
        :key="item.id"
        :grid-index="index"
        :index="vScroll.index + index"
        :scope-on-designer="scopeOnDesigner"
        :is-row-active="isRowActive(vScroll.index + index)"
        :remove-white-space="removeRowWhiteSpace(vScroll.index + index, index)"
      />
      <!-- Select range -->
      <range-selection
        v-for="(item, index) in ranges"
        :key="item.id"
        :item="item"
        :index="index"
        :options="options"
        :get-px-from-column-index="getPxFromColumnIndex"
        :get-px-from-row-index="getPxFromRowIndex"
      />
      <name-range-area
        v-for="(item, index) in namedRangeArea"
        :key="item.id"
        :visible="nameRangeVisible"
        :item="item"
        :index="index"
        :options="options"
        :get-px-from-column-index="getPxFromColumnIndex"
        :get-px-from-row-index="getPxFromRowIndex"
      />
      <!-- Arrows -->
      <arrows
        v-for="[row, rowIndex] in filteredRows"
        :key="row.id"
        :row-index="rowIndex"
        :row-id="row.id"
        :get-px-from-column-index="getPxFromColumnIndex"
        :get-px-from-row-index="getPxFromRowIndex"
        :get-index-at-row-px="getIndexAtRowPx"
        :get-index-at-column-px="getIndexAtColumnPx"
      ></arrows>

      <!-- Action Flow -->
      <custom-process-flow v-if="getColumnVisibility('Flow')" />

      <!-- Add Activity Button -->
      <custom-button
        v-if="!isGrid && players.length >= 2"
        data-test-id="add-activity-button"
        button-class="btn btn-default kk-designer__grid-activitybtn"
        :style="{ gridArea: buttonPosition }"
        :class="{ 'remove-cursor': !isDefaultCursor }"
        @click="addActivity"
      >
        <custom-icon class-name="icon-plus" />
      </custom-button>
    </div>
    <edit-cell
      v-if="(activeCellId && isInEditMode) || editCell.focus"
      :on-hover="showComment"
      :options="options"
      :get-px-from-column-index="getPxFromColumnIndex"
      :get-px-from-row-index="getPxFromRowIndex"
    />
  </div>
</template>

<script>
import { icons } from "../../utils/icons";
import hotkeys from "hotkeys-js";
import DesignerMixin from "../process-design/DesignerMixin";
import Arrows from "../process-design/body/tracks/Arrows.vue";
import { ColumnLetterHeight, CustomCursorTypes, EditModes, HotkeyScopes, RowNumberWidth } from "../../utils/constants";
import { ColumnTypes, RowTypes } from "../process-design/cell/CellConstants";
import CustomProcessFlow from "../process-design/body/sidebar/flow/CustomProcessFlow.vue";
import { sortRange, splitCellId, rangeIndexToLabel, rangeLabelToIndex, getCellId } from "../process-design/cell/Utils";
import {
  cellIndexToLabel,
  columnToIndex,
  indexToColumn,
  indexToRow,
  isCellWithinRange,
  rowToIndex,
  doRangesOverlap,
  isRange,
} from "../../utils/util";
import { DefaultHeights } from "../process-design/corner/CornerConstants";
import RangeSelection from "../process-design/RangeSelection.vue";
import NameRangeArea from "./NameRangeArea.vue";
import { ProcessTypes } from "../../utils/icons";
import CustomButton from "../commons/CustomButton.vue";
import CustomIcon from "../commons/CustomIcon.vue";
import EditCell from "../process-design/EditCell.vue";
import deepIs from "deep-is";
import ColumnLetter from "../process-design/coordinates/ColumnLetter.vue";
import RowNumber from "../process-design/coordinates/RowNumber.vue";
import HotkeyComponent from "../process-design/cell/HotkeyComponent.vue";
import { getMaxIndex } from "../../utils/array";

// get mouse position based on parent element table content and not the view port
function getMousePosition(event) {
  const tableContent = document.querySelector(".table-content");
  const parentRect = tableContent?.getBoundingClientRect();
  let x = event.clientX - parentRect?.left - RowNumberWidth; /* padding for row numbers */
  let y = event.clientY - parentRect?.top - ColumnLetterHeight; /* padding for column letters */

  if (x < 0 || y < 0 || !tableContent) return { x: -1, y: -1 };

  x += tableContent.scrollLeft;
  y += tableContent.scrollTop;

  return { x, y };
}

export default {
  components: {
    Arrows,
    CustomProcessFlow,
    RangeSelection,
    CustomButton,
    CustomIcon,
    EditCell,
    ColumnLetter,
    RowNumber,
    HotkeyComponent,
    NameRangeArea,
  },
  mixins: [DesignerMixin],
  props: [
    "visibleRows",
    "visibleColumns",
    "startYPosition",
    "startXPosition",
    "getPxFromRowIndex",
    "getPxFromColumnIndex",
    "getIndexAtColumnPx",
    "getIndexAtRowPx",
    "options",
  ],
  data() {
    return {
      lastHoveredCellId: null,
      arrowSet: false,
      scrollInterval: null,
      event: null,
    };
  },
  computed: {
    namedRangeArea() {
      const searchRange = this.mainRange?.range;
      const namedRanges = this.$store.getters["table/namedRangesForCurrentSheet"]();
      return namedRanges
        .map(({ name, expression }) => ({
          id: name,
          title: name,
          range: rangeLabelToIndex(expression),
        }))
        .filter((item) => {
          if (searchRange && isRange(rangeIndexToLabel(searchRange))) {
            return doRangesOverlap(item.range, searchRange);
          } else {
            return true;
          }
        });
    },
    nameRangeVisible() {
      return this.$store.getters["table/getNameRangeVisibility"]();
    },
    formatPainterData() {
      return {
        selectedRange: this.$store.state.cells.formatPainterRange,
        isFormatPainterActive: this.$store.state.cells.formatPainter,
      };
    },
    removeCursorInAddingActivity() {
      return (
        this.$store.state.cells.arrow.activeCursor === CustomCursorTypes.Start ||
        this.$store.state.cells.arrow.activeCursor === CustomCursorTypes.End
      );
    },
    scopeOnDesigner() {
      return this.$store.state.activeScope === HotkeyScopes.ProcessDesigner;
    },
    isDefaultCursor() {
      return (
        this.$store.state.cells.arrow.activeCursor === CustomCursorTypes.Default &&
        this.$store.state.customFlow.newArrow.activeCursor === CustomCursorTypes.Default
      );
    },
    isInEditMode() {
      return this.editMode !== EditModes.Disable;
    },
    editMode() {
      return this.$store.state.pdesign.editMode.value;
    },
    isGrid() {
      return this.current.type === ProcessTypes.Grid;
    },
    hScroll() {
      return this.$store.state.cells.hScroll;
    },
    vScroll() {
      return this.$store.state.cells.vScroll;
    },
    gridColumns() {
      return (
        RowNumberWidth +
        "px " +
        this.visibleColumns.reduce((pre, current) => pre + this.getRawWidth(current.id) + "px ", "")
      );
    },
    gridRows() {
      // 34 is for add activity button
      return (
        ColumnLetterHeight +
        "px " +
        this.visibleRows.reduce((pre, current) => pre + this.getRawHeight(current.id) + "px ", "") +
        " 34px"
      );
    },
    draggingArrow() {
      return this.$store.state.cells.draggingArrow;
    },
    activeRangeMainCellCoordinate() {
      return this.$store.state.cells.activeRangeMainCellCoordinate;
    },
    editCell() {
      return this.$store.state.cells.editCell;
    },
    ranges() {
      return this.$store.getters["cells/ranges"]();
    },
    visibleComment() {
      return this.$store.state.cells.visibleComment;
    },
    arrows() {
      return this.$store.state.cells.current?.design.arrows;
    },
    buttonPosition() {
      const activityIndex = this.columnMap["Activity"]?.index;
      return `${this.rows.length + 2} / ${activityIndex + 2} / span 1 / span 1`;
    },
    filteredRows() {
      const rows = this.visibleRows
        .map((item, index) => [item, index])
        .filter(([row]) => !this.isGrid && this.arrows[row.id] && this.getRowVisibility(row.id));
      return rows;
    },
    tableHeaders() {
      return this.$store.state.table.headers;
    },
    tableLastCellIds() {
      return this.$store.state.table.lastCellIds;
    },
    corsurType() {
      return this.$store.state.cells.rangeDragging.cursorType;
    },
    isFilterVisible() {
      return this.$store.state.table.filterDialogVisible;
    },
  },
  mounted() {
    this.styleOptions = this.options;
    document.addEventListener("mousemove", this.mouseMove);
    document.addEventListener("mouseleave", this.mouseLeave);
    document.addEventListener("mouseup", this.mouseUp);
    document.addEventListener("keydown", this.handleKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener("mousemove", this.mouseMove);
    document.removeEventListener("mouseleave", this.mouseLeave);
    document.removeEventListener("mouseup", this.mouseUp);
    document.removeEventListener("keydown", this.handleKeyDown);
    clearInterval(this.scrollInterval);
  },
  methods: {
    columnLetterClick(index) {
      const firstVisibleRowIndex = this.rows.findIndex((item) => this.getRowVisibility(item.id));
      const firstRowId = this.rows[firstVisibleRowIndex]?.id;
      const columnId = this.columns[index]?.id;
      var startColumnIndex = index;
      var endColumnIndex = index;
      const currentIndex = index;
      if (this.current.type === ProcessTypes.Posting && this.columns[index].type === ColumnTypes.Player) {
        const isMinus = /-minus$/i.test(columnId);
        [startColumnIndex, endColumnIndex] = isMinus
          ? [currentIndex - 1, currentIndex]
          : [currentIndex, currentIndex + 1];
      }
      this.$store.dispatch("cells/setFormulaRange", {
        title: `${indexToColumn(startColumnIndex)}${indexToRow(0)}:${indexToColumn(endColumnIndex)}${indexToRow(
          this.rows.length - 1
        )}`,
        activeCellId: getCellId(firstRowId, columnId),
      });
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", [index, firstVisibleRowIndex]);
    },
    rowNumberClick(index) {
      const firstVisibleColumnIndex = this.columns.findIndex((item) => this.getColumnVisibility(item.id));
      const firstColumnId = this.columns[firstVisibleColumnIndex]?.id;
      const rowId = this.rows[index]?.id;

      this.$store.dispatch("cells/setFormulaRange", {
        title: `${indexToColumn(0)}${indexToRow(index)}:${indexToColumn(this.columns.length - 1)}${indexToRow(index)}`,
        activeCellId: getCellId(rowId, firstColumnId),
      });
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", [firstVisibleColumnIndex, index]);
    },
    isColumnActive(index) {
      if (!this.mainRange) return;

      let range = this.mainRange?.range || [];
      return index >= range[0] && index <= range[2];
    },
    isRowActive(index) {
      if (!this.mainRange) return;

      let range = this.mainRange?.range || [];
      return index >= range[1] && index <= range[3];
    },
    removeColumnWhiteSpace(index, gridIndex) {
      if (!this.mainRange) return "unset";
      const startColumn = this.mainRange?.range[0];
      const endColumn = this.mainRange?.range[2];
      const distance = endColumn - startColumn;
      return this.isColumnActive(index) && gridIndex !== endColumn
        ? distance > 0
          ? "0.5rem 0px 0px 0px #ececec"
          : " "
        : "unset";
    },
    removeRowWhiteSpace(index, gridIndex) {
      if (!this.mainRange) return "unset";

      const startRow = this.mainRange?.range[1];
      const endRow = this.mainRange?.range[3];
      const distance = endRow - startRow;
      return this.isRowActive(index) && gridIndex !== this.mainRange?.range[3]
        ? distance > 0
          ? "0px 0.5rem 0px 0px #ececec"
          : "none"
        : "unset";
    },
    rightClick(e) {
      e.preventDefault();
      this.$store.dispatch("setActiveContextMenu", { id: this.id, x: e.x, y: e.y, type: "cell" });
    },
    dblClick(event) {
      let { x, y } = getMousePosition(event);
      if (x < 0 || y < 0 || this.arrowSet) return;

      let columnIndex = this.getIndexAtColumnPx(x);
      let rowIndex = this.getIndexAtRowPx(y);
      const id = this.getCellIdByIndex(rowIndex, columnIndex);
      if (!id) return;
      if (this.editMode === EditModes.Disable) {
        this.text = this.getCellText(rowIndex, columnIndex);
        this.$store.dispatch("pdesign/setEditMode", EditModes.InnerEdit);
      }
    },
    handleKeyDown(event) {
      // Only trigger if Alt + ArrowDown
      if (event.altKey && event.key === "ArrowDown") {
        event.preventDefault();
        this.showFilterDialog(event);
      }
      if (event.ctrlKey && event.shiftKey && event.key.toLowerCase() === "l") {
        event.preventDefault();
        const table = this.$store.state.table.activeTable;
        if (!table) return;
        this.$store.dispatch("table/toggleFilterButtonVisibility", table);
      }
    },

    showFilterDialog(event) {
      const id = this.$store.state.table.selectedCellId;
      if (!id || !this.isGrid || !this.tableHeaders[id]?.visible) return;

      const [rowId, columnId] = splitCellId(id);
      const columnIndex = this.columnMap[columnId]?.index;
      const rowIndex = this.rowMap[rowId]?.index;
      if (columnIndex == null || rowIndex == null) return;

      const columnPosition = this.getPxFromColumnIndex(columnIndex);
      const rowPosition = this.getPxFromRowIndex(rowIndex);
      const columnWidth = this.getRawWidth(columnId);
      const rowHeight = this.getRawHeight(rowId);

      const container = document.querySelector(".table-content");

      const distanceFromTop = rowHeight - 18;
      const distanceFromLeft = rowHeight - 12;

      this.$store.dispatch("table/setFilterDialogPosition", {
        x: columnPosition + columnWidth + RowNumberWidth - distanceFromLeft - container.scrollLeft,
        y: rowPosition + DefaultHeights.Grid + distanceFromTop,
      });

      this.$store.dispatch("table/setFilterDialogVisible", true);
    },
    async cellClick(event) {
      const isFormulaMode = this.editCell.formulaRangeSelectionAllowed && this.editCell.text.trim().startsWith("=");

      // If not formula we use setTimeout because order or saving and rangeSelection for undo redo
      if (!isFormulaMode) {
        await this.$store.dispatch("pdesign/setEditMode", EditModes.Disable);

        setTimeout(() => {
          this.handleCellClickCore(event);
        }, 0);
      } else {
        this.handleCellClickCore(event);
      }
    },
    handleCellClickCore(event) {
      // to make sure double click while setting arrow doesn't go into inner edit mode
      if (event.target.closest(".icon-dots-horizontal")) return;
      if (!this.isDefaultCursor) {
        this.arrowSet = true;
        setTimeout(() => {
          this.arrowSet = false;
        }, 500);
      }
      if (this.isFilterVisible) return;

      let { x, y } = getMousePosition(event);
      if (x < 0 || y < 0) return;

      let columnIndex = this.getIndexAtColumnPx(x);
      let rowIndex = this.getIndexAtRowPx(y);
      const id = this.getCellIdByIndex(rowIndex, columnIndex);
      if (!id) return;
      this.$store.dispatch("table/setActiveTable", id);

      const columnPosition = this.getPxFromColumnIndex(columnIndex);
      const rowPosition = this.getPxFromRowIndex(rowIndex);
      const [rowId, columnId] = splitCellId(id);
      let columnWidth = this.getRawWidth(columnId);
      let rowHeight = this.getRawHeight(rowId);
      if (this.isGrid && this.tableHeaders[id]?.visible) {
        const container = document.querySelector(".table-content");
        if (x < columnPosition + columnWidth && x > columnPosition + columnWidth - 18) {
          // These values are for positioning the filter options on the middle of toggle icon.
          const distanceFromTop = rowHeight - 16;
          const distanceFromLeft = rowHeight - 10;
          this.$store.dispatch("table/setFilterDialogPosition", {
            x: columnPosition + columnWidth + RowNumberWidth - distanceFromLeft - container.scrollLeft,
            y: rowPosition + DefaultHeights.Grid + distanceFromTop,
          });
          this.$store.dispatch("table/setFilterDialogVisible", true);
          this.$store.dispatch("table/setSelectedCellId", id);
        } else if (
          x < columnPosition + columnWidth &&
          x > columnPosition - 38 &&
          y < rowPosition + rowHeight / 3 &&
          y > rowPosition
        ) {
          this.$store.dispatch("table/columnRangeSelection", id);
          return;
        }
      } else if (this.isGrid && this.tableLastCellIds[id]) {
        if (
          x < columnPosition + columnWidth &&
          x > columnPosition + columnWidth - 14 &&
          y < rowPosition + rowHeight &&
          y > rowPosition + rowHeight / 3
        ) {
          return this.updateTableRange();
        }
      }

      if (this.isFilterVisible) return;
      this.$store.dispatch("table/setSelectedCellId", id);
      const mergeId = this.mergedCellIds[id] ?? id;
      const isMerged = mergeId.includes("_merged");
      if (isMerged) {
        const [rowId, columnId] = splitCellId(mergeId);
        columnIndex = this.columnMap[columnId].index;
        rowIndex = this.rowMap[rowId].index;
      }
      const mergedCell = this.mergedCells[mergeId];

      if (
        rowIndex < this.vScroll.index ||
        rowIndex > this.vScroll.end ||
        columnIndex < this.hScroll.index ||
        columnIndex > this.hScroll.end
      ) {
        return;
      }

      this.$store.dispatch("setScope", HotkeyScopes.ProcessDesigner); // for toggling active scope. ctrl+\

      const { rowSpan, colSpan } = !mergedCell
        ? { rowSpan: 1, colSpan: 1 }
        : this.$store.getters["cells/getRowAndColSpan"](mergedCell);

      const rowDelta = isMerged ? rowSpan - 1 : 0;
      const columnDelta = isMerged ? colSpan - 1 : 0;

      if (event.shiftKey && this.activeRangeMainCellCoordinate) {
        this.selectRange(columnIndex, rowIndex);
        return;
      }
      this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", this.getCellCoordinate(id));
      if (this.editCell.formulaRangeSelectionAllowed || this.editCell.range) {
        event.preventDefault();
        const title = `${cellIndexToLabel(columnIndex, rowIndex)}`;
        return this.$store.dispatch("cells/addFormulaRange", title);
      }
      this.$store.dispatch("rangeMovement/updateMovement", {
        rangeLabel: `${cellIndexToLabel(columnIndex, rowIndex)}:${cellIndexToLabel(
          columnIndex + columnDelta,
          rowIndex + rowDelta
        )}`,
        activeCellId: id.replace("_merged", ""),
      });
      this.setArrow(event.clientX, columnIndex, rowIndex);
    },
    updateTableRange() {
      const title = this.$store.state.table.activeTable.tableRange;
      const activeRangeId = this.$store.dispatch("cells/setFormulaRange", { title, isMainRange: false });
      this.$store.dispatch("cells/updateActiveRange", { id: activeRangeId, title, mode: "fill" });
      this.$store.dispatch("pdesign/setEditMode", EditModes.Disable);
      this.$store.dispatch("cells/setRangeDragging", {
        id: activeRangeId,
        range: rangeLabelToIndex(title),
        drag: "table",
      });
    },
    async setArrow(clientX, columnIndex, rowIndex) {
      const column = this.columns[columnIndex];
      const row = this.rows[rowIndex];
      if (
        this.$store.state.cells.arrow.activeCursor === CustomCursorTypes.Default ||
        column.type !== ColumnTypes.Player ||
        row.type !== RowTypes.Activity
      )
        return;
      // after creating a new activity, change cursor type to add an arrow
      await this.$store.dispatch("cells/setArrow", {
        rowId: row.id,
        columnId: column.id,
        x: clientX,
      });
    },
    showComment(cellId) {
      const cell = this.mergedCellIds[cellId]
        ? this.cellsMetaData[this.mergedCellIds[cellId].replace("_merged", "")]
        : this.cellsMetaData[cellId];
      const checkCellId = this.mergedCellIds[cellId] ? this.mergedCellIds[cellId].replace("_merged", "") : cellId;
      setTimeout(() => {
        if (!cell?.comment && !this.visibleComment.editMode) {
          this.$store.dispatch("cells/setComment", { id: null, editMode: false });
        } else if (!this.visibleComment.editMode && cell.comment && this.visibleComment.id !== checkCellId) {
          this.$store.dispatch("cells/setComment", { id: checkCellId, editMode: false });
        }
      }, 200);
    },
    hideComment() {
      if (!this.visibleComment.editMode) this.$store.dispatch("cells/setComment", { id: null, editMode: false });
    },
    addActivity() {
      const range = this.mainRange.range;
      // row index is one more the last index because the activity needs to be added at the end
      this.$store.dispatch("cells/addRow", {
        type: RowTypes.Activity,
        customRange: [range[0], this.rows.length, range[2], this.rows.length],
      });
    },
    async mouseUp() {
      if (this.formatPainterData.isFormatPainterActive) {
        let selectedRange = this.$store.getters["cells/activeRange"]()?.range;

        this.$store.dispatch("rangeStyle/updateFillStyle", {
          initialRange: this.formatPainterData.selectedRange.range,
          newRange: selectedRange,
          isFormatPainter: true,
        });
      }

      if (this.rangeDragging?.drag === "range") {
        this.$store.dispatch("cells/fill", {
          initialRange: this.rangeDragging.range,
          newRange: this.$store.getters["cells/activeRange"]()?.range,
        });
      }
      if (this.rangeDragging?.drag === "table") {
        const activeCellId = this.$store.state.cells.activeCellId;
        const mainRangeTitle = rangeIndexToLabel(this.mainRange.range);
        const newRange = this.$store.getters["cells/activeRange"]()?.range;
        const activeTable = this.$store.state.table.activeTable;

        const updatedTable = { ...activeTable, tableRange: rangeIndexToLabel(newRange) };
        const isRangeConflict = this.$store.getters["table/tableRangeConflict"]({
          range: newRange,
          table: updatedTable,
        });
        //TODO: show conflict error message here
        if (!isRangeConflict) {
          this.$store.dispatch("table/updateTableDetails", { updatedTable, oldTable: activeTable });
        }

        this.$store.dispatch("cells/resetFormulaRange");
        this.$store.dispatch("cells/setActiveRangeMainCellCoordinate", this.getCellCoordinate(activeCellId));
        this.$store.dispatch("cells/setFormulaRange", {
          title: mainRangeTitle,
          isMainRange: true,
          activeCellId,
        });
        this.$store.dispatch("cells/setRangeDragging", null);
      }
      clearInterval(this.scrollInterval);
      if (this.draggingArrow.arrowId) this.$store.dispatch("cells/resetDraggingArrow");
      this.$store.dispatch("cells/setStartDragging", false);
    },
    cellMouseEnter(e) {
      const id = e.target.closest(".kk-designer__cell")?.dataset?.id;
      if (id && id !== this.lastHoveredCellId) {
        const cell = this.cellsMetaData[id] || {};
        this.showComment(cell, id);
      }
      this.lastHoveredCellId = id;
    },
    mouseLeave(e) {
      e.preventDefault();
      clearInterval(this.scrollInterval);
    },
    selectRange(columnIndex, rowIndex) {
      const newRange = this.calculateRange(sortRange([...this.activeRangeMainCellCoordinate, columnIndex, rowIndex]));
      const rangeLabel = rangeIndexToLabel(newRange);

      let activeRange = this.$store.getters["cells/activeRange"]();
      const isMainRange = deepIs(this.mainRange.range, activeRange.range);

      if (deepIs(newRange, this.mainRange.range)) return;

      if (isMainRange) this.$store.dispatch("cells/setFormulaRange", { title: rangeLabel, isMainRange });
      else this.$store.dispatch("cells/updateActiveRange", { id: activeRange.id, title: rangeLabel, isMainRange });
    },
    isOutsideBounds(e, rect) {
      return (
        e.clientX < rect.left + RowNumberWidth ||
        e.clientX > rect.right - 15 ||
        e.clientY < rect.top + ColumnLetterHeight ||
        e.clientY > rect.bottom - 15
      );
    },
    triggerScroll(rect) {
      const activeRange = this.$store.getters["cells/activeRange"]()?.range;
      const range = this.rangeDragging?.range;

      // used for dragging series
      const isHorizontal = !this.rangeDragging?.drag || (activeRange[1] === range[1] && activeRange[3] === range[3]);
      const isVertical = !this.rangeDragging?.drag || (activeRange[0] === range[0] && activeRange[2] === range[2]);

      const clientX = this.event.clientX;
      const clientY = this.event.clientY;

      //TODO: need to handle scrolling for table dragging
      if (this.rangeDragging.drag === "table") return;
      if (clientX < rect.left + RowNumberWidth && isHorizontal) {
        hotkeys.trigger("shift+left", HotkeyScopes.ProcessDesigner);
      }
      if (clientX > rect.right - 15 && isHorizontal) {
        hotkeys.trigger("shift+right", HotkeyScopes.ProcessDesigner);
      }
      if (clientY < rect.top + ColumnLetterHeight && isVertical) {
        hotkeys.trigger("shift+up", HotkeyScopes.ProcessDesigner);
      }
      if (clientY > rect.bottom - 15 && isVertical) {
        hotkeys.trigger("shift+down", HotkeyScopes.ProcessDesigner);
      }
    },
    mouseMove(e) {
      // e.preventDefault(); // It was preventing drag and drop of players.
      // if an arrow is dragging then change x position
      if (this.draggingArrow.arrowId !== null) {
        return this.$store.dispatch("cells/setDraggingArrow", { ...this.draggingArrow, x: e.clientX });
      }

      let activeRange = this.$store.getters["cells/activeRange"]()?.range;
      let activeRangeId = this.$store.getters["cells/activeRange"]()?.id;

      const tableContent = document.querySelector(".table-content");
      const parentRect = tableContent?.getBoundingClientRect();

      this.event = e;
      // Scrolling with mouse
      if (!this.scrollInterval && this.startDragging && parentRect && this.isOutsideBounds(e, parentRect)) {
        clearInterval(this.scrollInterval);
        this.scrollInterval = setInterval(() => this.triggerScroll(parentRect), 50);
      } else if (parentRect && !this.isOutsideBounds(e, parentRect)) {
        clearInterval(this.scrollInterval);
        this.scrollInterval = null;
      }

      let { x, y } = getMousePosition(e);
      if (x < 0 || y < 0) return;
      const columnIndex = this.getIndexAtColumnPx(x);
      const rowIndex = this.getIndexAtRowPx(y);
      const id = this.getCellIdByIndex(rowIndex, columnIndex);
      let corsurType = "";

      if (this.isGrid && (this.tableHeaders[id]?.visible || this.tableLastCellIds[id])) {
        const columnPosition = this.getPxFromColumnIndex(columnIndex);
        const rowPosition = this.getPxFromRowIndex(rowIndex);
        const [rowId, columnId] = splitCellId(id);
        let columnWidth = this.getRawWidth(columnId);
        let rowHeight = this.getRawHeight(rowId);
        if (this.tableHeaders[id]) {
          if (x < columnPosition + columnWidth && x > columnPosition + columnWidth - 18) {
            corsurType = "pointer";
            this.$store.state.cells.rangeDragging.cursorType = corsurType;
            return;
          } else if (
            x < columnPosition + columnWidth &&
            x > columnPosition - 38 &&
            y < rowPosition + rowHeight / 3 &&
            y > rowPosition
          ) {
            // change cursor type
            corsurType = "s";
            this.$store.state.cells.rangeDragging.cursorType = corsurType;
            return;
          }
        } else if (
          x < columnPosition + columnWidth &&
          x > columnPosition + columnWidth - 14 &&
          y < rowPosition + rowHeight &&
          y > rowPosition + rowHeight / 3
        ) {
          corsurType = "nwse";
          this.$store.state.cells.rangeDragging.cursorType = corsurType;
          return;
        }
      }
      this.$store.state.cells.rangeDragging.cursorType = corsurType;
      this.showComment(id);
      if (!id || !activeRange || !this.startDragging) return;

      if (!this.rangeDragging.id) {
        this.selectRange(columnIndex, rowIndex);
      } else {
        if (
          (["topleft", "bottomleft"].includes(this.rangeDragging.position) && columnIndex > activeRange[2]) ||
          (["topright", "bottomright"].includes(this.rangeDragging.position) && columnIndex < activeRange[0])
        ) {
          return this.$store.dispatch("cells/swapRangePosition", "x");
        }

        if (
          (["topleft", "topright"].includes(this.rangeDragging.position) && rowIndex > activeRange[3]) ||
          (["bottomleft", "bottomright"].includes(this.rangeDragging.position) && rowIndex < activeRange[1])
        ) {
          return this.$store.dispatch("cells/swapRangePosition", "y");
        }

        let newRange, mode;
        if (this.rangeDragging.drag === "range") {
          newRange = this.calculateFillRange(columnIndex, rowIndex);
          mode = "fill";
        } else if (this.rangeDragging.drag === "table") {
          const baseRange = this.rangeDragging.range;
          if (columnIndex < baseRange[0] || rowIndex < baseRange[1] + 2) return; // return if table is one column or one row.

          newRange = this.calculateTableRange(columnIndex, rowIndex);
          mode = "fill";
        } else {
          newRange = this.calculateFormulaRange(columnIndex, rowIndex, activeRange);
        }
        const rangeLabel = rangeIndexToLabel(this.calculateRange(newRange));
        this.$store.dispatch("cells/updateActiveRange", { id: activeRangeId, title: rangeLabel, mode });
        // in order to force focus and set cursor position on edit cell
        this.$store.dispatch("cells/setRangeDragging", this.rangeDragging);
      }
    },
    calculateTableRange(columnIndex, rowIndex) {
      const baseRange = this.rangeDragging.range;
      const newRange = [...baseRange];
      if (Math.abs(columnIndex - baseRange[2]) >= Math.abs(rowIndex - baseRange[3])) newRange[2] = columnIndex;
      else newRange[3] = rowIndex;

      return newRange;
    },
    calculateFillRange(columnIndex, rowIndex) {
      const baseRange = this.rangeDragging.range;
      if (
        columnIndex >= baseRange[0] &&
        columnIndex <= baseRange[2] &&
        rowIndex >= baseRange[1] &&
        rowIndex <= baseRange[3]
      )
        return baseRange;

      const diffs = [
        baseRange[0] - columnIndex,
        baseRange[1] - rowIndex,
        columnIndex - baseRange[2],
        rowIndex - baseRange[3],
      ];
      const maxDiff = getMaxIndex(diffs);

      const newRange = [...baseRange];
      if (maxDiff === 0) {
        newRange[0] = columnIndex;
      } else if (maxDiff === 1) {
        newRange[1] = rowIndex;
      } else if (maxDiff === 2) {
        newRange[2] = columnIndex;
      } else if (maxDiff === 3) {
        newRange[3] = rowIndex;
      }
      return newRange;
    },
    calculateFormulaRange(columnIndex, rowIndex, activeRange) {
      const range1 = [columnIndex, rowIndex, columnIndex, rowIndex];
      const changes = {
        topleft: [true, true, false, false],
        topright: [false, true, true, false],
        bottomleft: [true, false, false, true],
        bottomright: [false, false, true, true],
      };
      return changes[this.rangeDragging.position].map((item, i) => (item ? range1[i] : activeRange[i]));
    },
    mouseDown(e) {
      if (this.rangeDragging?.id) return this.$store.dispatch("cells/setStartDragging", true);

      this.$store.dispatch("cells/setStartDragging", true);
      const range = this.mainRange?.range;
      let isClickWithinSelectedRange = true;
      if (range) {
        const { x, y } = getMousePosition(e);
        const xIndex = this.getIndexAtColumnPx(x);
        const yIndex = this.getIndexAtRowPx(y);
        isClickWithinSelectedRange = isCellWithinRange([xIndex, yIndex], range);
      }
      if (e.target.classList.contains("kk-designer__columnletter")) {
        const index = columnToIndex(e.target.innerHTML);
        this.columnLetterClick(index);
      } else if (e.target.classList.contains("kk-designer__rowletter")) {
        const index = rowToIndex(+e.target.innerHTML);
        this.rowNumberClick(index);
      }
      // 0: Main button pressed, usually the left button or the un-initialized state.
      // 1: Auxiliary button pressed, usually the middle button (if present).
      // 2: Secondary button pressed, usually the right button.
      if (e.button === 0 || (e.button === 2 && !isClickWithinSelectedRange)) this.cellClick(e);
    },
    resetSelection() {
      this.$store.dispatch("cells/resetFormulaRange");
    },
    include() {
      return [
        document.querySelector(".kk-designer__info"),
        document.querySelector(".kk-designer__activity-menu"),
        document.querySelector(".kk-panel"),
        document.querySelector(".contextmenu"),
        document.querySelector(".kk_goto-cell"),
        document.querySelector(".kk-series"),
        document.querySelector(".kk__error"),
        ...document.querySelectorAll(".kk_searchmodal-search_item"),
      ];
    },
  },
};
</script>
<style lang="less" scoped>
@import "../../assets/styles/variables";
.wrapper {
  display: inline-block;
  width: 100%;
}
.kk-designer__grid {
  padding: 0 1rem 1rem 0;
  display: grid;
  align-items: stretch;
  position: absolute;
  left: 0;
  pointer-events: auto;
  cursor: cell;
  &--removeCursor {
    cursor: none;
  }
  &-nwse {
    cursor: nwse-resize;
  }
  &-nesw {
    cursor: nesw-resize;
  }
  &-s {
    cursor: s-resize;
  }
  &-se {
    cursor: se-resize;
  }
  &-e {
    cursor: e-resize;
  }
  &--formatPainter {
    cursor: copy;
  }
  &-crosshair {
    cursor: crosshair;
  }
  &-pointer {
    cursor: pointer;
  }
  &-activitybtn {
    background-color: #fff;
    height: 30px;
    border-radius: 0.25rem;
    transition: all 0.2s;
    margin: 0.2rem;
  }
  &-mergedCell {
    z-index: 2;
  }
  &-range {
    border: 2px solid @kk-color-primary-blue;
    border-radius: 0.25rem;
    background-color: rgba(13, 102, 154, 0.2);
    z-index: 3;
    pointer-events: none;
  }
}
</style>
